import { Accordion, AccordionSummary, Typography, AccordionDetails, TableContainer, Table, TableBody, TableRow, TableCell, FormControlLabel, Radio, Switch, Link, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CardsPricingModal from '../../../pmui/onboarding/components/CardsPricingModal';
import {
  ProductTypeEnum,
  PaymentsProductDto,
  PaymentTypeEnum,
  PaidByEnum,
  AmountTier,
  PricingQuoteDetailsDto,
  PricingQuoteIncludedProductDto,
  ConfiguredPaymentOptionDto,
  FeeTypeEnum,
} from '@inosio/payments-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { buildPricingQuoteDto, selectOnboardingApplicationState } from '../../../pmui/onboarding/sagas/pmonboardingSlice';
import { ProductPreferencesProps } from '../../interfaces/ProductPreferenceInterface';
import HelpIcon from '@mui/icons-material/Help';

const PaymentsPreferences = (props: ProductPreferencesProps) => {
  const selectOnboardingState = useSelector(selectOnboardingApplicationState);
  const dispatch = useDispatch();
  const availablePaymentsProduct = props.availableProducts?.[0] as PaymentsProductDto; //theres always only one for now
  //existing selections
  const pricingQuotePaymentsProductDto = selectOnboardingState.PricingQuoteSetupObj.IncludedProducts.find((prod) => prod.ProductUuid === availablePaymentsProduct.ProductUuid);
  const existingAchSelection = pricingQuotePaymentsProductDto?.PaymentOptions?.find((po) => po.PaymentType === PaymentTypeEnum.Ach);
  const existingDbSelection = pricingQuotePaymentsProductDto?.PaymentOptions?.find((po) => po.PaymentType === PaymentTypeEnum.Db);
  const existingCcSelection = pricingQuotePaymentsProductDto?.PaymentOptions?.find((po) => po.PaymentType === PaymentTypeEnum.Cc);
  const collectiveExsitingCardSelection = existingDbSelection && existingCcSelection ? [existingDbSelection, existingCcSelection] : [];

  //local state
  const [achConfig, setAchConfig] = useState<ConfiguredPaymentOptionDto | undefined>(existingAchSelection);
  const [cardConfigs, setCardConfigs] = useState<ConfiguredPaymentOptionDto[]>(collectiveExsitingCardSelection);
  const [isPaymentsSelected, setIsPaymentsSelected] = useState(!!pricingQuotePaymentsProductDto);
  const [openCardPricingModal, setOpenCardPricingModal] = useState(false);

  //local vars
  const achPricing = availablePaymentsProduct.AvailableOptions.find((option) => option.PaymentType === PaymentTypeEnum.Ach);
  const ccPricing = availablePaymentsProduct.AvailableOptions.find((option) => option.PaymentType === PaymentTypeEnum.Cc);
  const dbPricing = availablePaymentsProduct.AvailableOptions.find((option) => option.PaymentType === PaymentTypeEnum.Db);

  const dbTiersToBePaidByPm = dbPricing?.ConfigurationOptions.find((config) => config.PaidBy === PaidByEnum.Pm)?.Fee as AmountTier[];
  const dbTiersToBePaidByPayer = dbPricing?.ConfigurationOptions.find((config) => config.PaidBy === PaidByEnum.Payer)?.Fee as AmountTier[];

  const ccPercToBePaidByPm = ccPricing?.ConfigurationOptions.find((config) => config.PaidBy === PaidByEnum.Pm)?.Fee as AmountTier[];
  const ccPercToBePaidByPayer = ccPricing?.ConfigurationOptions.find((config) => config.PaidBy === PaidByEnum.Payer)?.Fee as AmountTier[];
  const ccCovenienceFeeToBePaidByPayer = ccPricing?.ConfigurationOptions.find((config) => config.PaidBy === PaidByEnum.Payer)?.ConvenienceFee as number;
  const ccCovenienceFeeToBePaidByPm = ccPricing?.ConfigurationOptions.find((config) => config.PaidBy === PaidByEnum.Pm)?.ConvenienceFee as number;

  const achAmountToBePaidByPm = achPricing?.ConfigurationOptions.find((config) => config.PaidBy === PaidByEnum.Pm)?.Fee as AmountTier[];
  const achAmountToBePaidByPayer = achPricing?.ConfigurationOptions.find((config) => config.PaidBy === PaidByEnum.Payer)?.Fee as AmountTier[];
  const achAdditionalFeaturesByPm = achPricing?.ConfigurationOptions.find((config) => config.PaidBy === PaidByEnum.Pm)?.AdditionalFeatures;
  const achAdditionalFeaturesByPayer = achPricing?.ConfigurationOptions.find((config) => config.PaidBy === PaidByEnum.Payer)?.AdditionalFeatures;

  const isPaymentsPreferencesDtoValid = props.validator.IsPreferenceObjectComplete(pricingQuotePaymentsProductDto);

  //useffects

  /**
   * This useffect listens for changes in the isPaymentsSelected (whether product is removed or added) state and whether any configs have been changed.
   * It then makes changes to the PricingQuoteSetupObj based on this state.
   */
  useEffect(() => {
    //fetch all products that were selected earlier in our master object
    const existingSelectedProducts: PricingQuoteIncludedProductDto[] = selectOnboardingState.PricingQuoteSetupObj.IncludedProducts || [];

    let updatedSelectedProductsList = [...existingSelectedProducts];

    const configuredPaymentOptions: ConfiguredPaymentOptionDto[] = [];

    //if ach was configured add that to the config list
    if (achConfig) configuredPaymentOptions.push(achConfig);

    //if cards were configured, add them. Since we collectively configure both db and cc, make sure theres always 2 configs for cards.
    if (cardConfigs.length === 2) configuredPaymentOptions.push(...cardConfigs);

    //if payments have been turned on
    if (isPaymentsSelected) {
      //prepare the payments object to be added to selected products list
      const productObjToAdd: PricingQuoteIncludedProductDto = {
        ProductUuid: availablePaymentsProduct.ProductUuid,
        ProductType: ProductTypeEnum.Payments,
        ProductLabel: 'Payments',
      };

      //if any payment options were configured
      if (configuredPaymentOptions.length > 0) {
        // add configurations to the obj
        productObjToAdd.PaymentOptions = configuredPaymentOptions;
      }

      // Add the new product selection if it doesn't already exist
      if (!updatedSelectedProductsList.some((prod) => prod.ProductUuid === availablePaymentsProduct.ProductUuid)) {
        updatedSelectedProductsList.push(productObjToAdd);
      } else {
        // Replace the existing product if it already exists
        updatedSelectedProductsList = existingSelectedProducts.map((prod: PricingQuoteIncludedProductDto) => (prod.ProductUuid === availablePaymentsProduct.ProductUuid ? productObjToAdd : prod));
      }
    } else {
      // Payments has been turned off, remove the payments product. Also remove any corequisite products if any were selected
      const productsToRemove = new Set<string>();

      // Firstly, add the payments product to the removal set
      productsToRemove.add(availablePaymentsProduct.ProductUuid);

      // then add any co-requisite products to the removal set
      props.coRequisiteProducts.forEach((coReqProd) => {
        productsToRemove.add(coReqProd.ProductUuid);
      });

      // Filter the list to exclude products with Uuids in the removal set
      updatedSelectedProductsList = existingSelectedProducts.filter((prod: PricingQuoteIncludedProductDto) => !productsToRemove.has(prod.ProductUuid));
    }

    dispatch({
      type: buildPricingQuoteDto.type,
      payload: {
        IncludedProducts: updatedSelectedProductsList,
      } as PricingQuoteDetailsDto,
    });
  }, [achConfig, cardConfigs, isPaymentsSelected]);

  /**
   * This useffect listens for any changes in our master obj and checks if payments has been added.
   * If it has then it turns on the toggle otherwise turns it off
   */
  useEffect(() => {
    const selectedPaymentsProduct = selectOnboardingState.PricingQuoteSetupObj.IncludedProducts.find((prod) => prod.ProductUuid === availablePaymentsProduct.ProductUuid);
    if (selectedPaymentsProduct) {
      setIsPaymentsSelected(true);
    } else {
      setIsPaymentsSelected(false);
    }
  }, [selectOnboardingState.PricingQuoteSetupObj]);

  return (
    <Box>
      <Accordion sx={{ marginTop: 2 }} elevation={0} defaultExpanded={true}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ marginTop: 2, boxShadow: 'none', '& .MuiPaper-root': { boxShadow: 'none' } }}>
          <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
            <Switch
              checked={isPaymentsSelected}
              onChange={(e) => {
                if (props.isCustomMode) {
                  setIsPaymentsSelected(e.target.checked);
                  //if they turn off payments clear out all the selections
                  if (!e.target.checked) {
                    setAchConfig(undefined);
                    setCardConfigs([]);
                  }
                }
              }}
            />
            <Typography sx={{ fontFamily: 'Work Sans !important', fontWeight: 600, fontSize: 'large' }}>Payments</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table
              sx={{
                border: 'none',
                '& .MuiTableCell-root': {
                  padding: { xs: 1, sm: 2 },
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                },
              }}
            >
              <TableBody>
                {/* ACH Payments Row */}
                <TableRow>
                  <TableCell sx={{ border: 'none', display: 'flex' }}>
                    <Typography sx={{ fontFamily: 'Work Sans !important', color: props.inValidationMode && !isPaymentsPreferencesDtoValid && !achConfig ? 'red' : 'black' }}>ACH Payments:</Typography>
                    <Tooltip title={'Fees are for ACH payments up to $7500 per payment. Higher limits available, but subject to pricing adjustments.'}>
                      <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: 1 }} />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ border: 'none' }}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={achConfig?.PaidBy === PaidByEnum.Payer}
                          onChange={() => {
                            //if they select some options automatically turn on the switch
                            setIsPaymentsSelected(true);
                            const configObj: ConfiguredPaymentOptionDto = {
                              PaymentType: PaymentTypeEnum.Ach,
                              PaidBy: PaidByEnum.Payer,
                              Fee: achAmountToBePaidByPayer,
                              FeeType: FeeTypeEnum.F,
                              AdditionalFeatures: achAdditionalFeaturesByPayer,
                            };
                            setAchConfig(configObj);
                          }}
                        />
                      }
                      label={`Payers pay $${(achAmountToBePaidByPayer[0].Amount / 100).toFixed(2)}`} //take the amount from the first tier, theres only one for ACH
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontFamily: 'Work Sans !important',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: 'none' }}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={achConfig?.PaidBy === PaidByEnum.Pm}
                          onChange={() => {
                            setIsPaymentsSelected(true);
                            const configObj: ConfiguredPaymentOptionDto = {
                              PaymentType: PaymentTypeEnum.Ach,
                              PaidBy: PaidByEnum.Pm,
                              Fee: achAmountToBePaidByPm,
                              FeeType: FeeTypeEnum.F,
                              AdditionalFeatures: achAdditionalFeaturesByPm,
                            };
                            setAchConfig(configObj);
                          }}
                        />
                      }
                      label={`I'll pay $${(achAmountToBePaidByPm[0].Amount / 100).toFixed(2)}`} //take the amount from the first tier, theres only one for ACH
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontFamily: 'Work Sans !important',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: 'none' }}></TableCell>
                </TableRow>

                {/* Card Payments Row */}
                <TableRow>
                  <TableCell sx={{ border: 'none' }}>
                    <Typography sx={{ fontFamily: 'Work Sans !important', color: props.inValidationMode && !isPaymentsPreferencesDtoValid && cardConfigs.length !== 2 ? 'red' : 'black' }}>
                      Card Payments:
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ border: 'none' }}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={cardConfigs.length === 2 && cardConfigs.every((config) => config.PaidBy === PaidByEnum.Payer)}
                          onChange={() => {
                            setIsPaymentsSelected(true);
                            const dbConfigObj: ConfiguredPaymentOptionDto = {
                              PaymentType: PaymentTypeEnum.Db,
                              PaidBy: PaidByEnum.Payer,
                              Fee: dbTiersToBePaidByPayer,
                              FeeType: FeeTypeEnum.T,
                            };
                            const ccConfigObj: ConfiguredPaymentOptionDto = {
                              PaymentType: PaymentTypeEnum.Cc,
                              PaidBy: PaidByEnum.Payer,
                              Fee: ccPercToBePaidByPayer,
                              FeeType: FeeTypeEnum.P,
                              ConvenienceFee: ccCovenienceFeeToBePaidByPayer,
                            };

                            setCardConfigs([dbConfigObj, ccConfigObj]);
                          }}
                        />
                      }
                      label="Payers pay fees"
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontFamily: 'Work Sans !important',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: 'none' }}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={cardConfigs.length === 2 && cardConfigs.every((config) => config.PaidBy === PaidByEnum.Pm)}
                          onChange={() => {
                            setIsPaymentsSelected(true);
                            const dbConfigObj: ConfiguredPaymentOptionDto = {
                              PaymentType: PaymentTypeEnum.Db,
                              PaidBy: PaidByEnum.Pm,
                              Fee: dbTiersToBePaidByPm,
                              FeeType: FeeTypeEnum.T,
                            };
                            const ccConfigObj: ConfiguredPaymentOptionDto = {
                              PaymentType: PaymentTypeEnum.Cc,
                              PaidBy: PaidByEnum.Pm,
                              Fee: ccPercToBePaidByPm,
                              FeeType: FeeTypeEnum.P,
                              ConvenienceFee: ccCovenienceFeeToBePaidByPm,
                            };
                            setCardConfigs([dbConfigObj, ccConfigObj]);
                          }}
                        />
                      }
                      label="I'll cover the fees"
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontFamily: 'Work Sans !important',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: 'none' }}>
                    <Link
                      onClickCapture={() => {
                        setOpenCardPricingModal(true);
                      }}
                      underline="hover"
                      sx={{ fontFamily: 'Work Sans !important', cursor: 'pointer' }}
                    >
                      View Fees
                    </Link>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      <CardsPricingModal
        open={openCardPricingModal}
        onClose={() => {
          setOpenCardPricingModal(false);
        }}
        debitTiers={cardConfigs.every((config) => config.PaidBy === PaidByEnum.Pm) ? dbTiersToBePaidByPm : dbTiersToBePaidByPayer}
        CcPercentage={cardConfigs?.every((config) => config.PaidBy === PaidByEnum.Pm) ? ccPercToBePaidByPm[0].Amount : ccPercToBePaidByPayer[0].Amount} //take the amount from the first tier, theres only one for Cc
        ccConvenienceFee={cardConfigs?.every((config) => config.PaidBy === PaidByEnum.Pm) ? ccCovenienceFeeToBePaidByPm : ccCovenienceFeeToBePaidByPayer}
      />
    </Box>
  );
};
export default PaymentsPreferences;
